import React, { useState, useEffect, useRef } from 'react';
import { Button, message, Input, Typography, Space } from 'antd';
import { diffArrays } from 'diff';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

import { sanitizeJson } from '../../utils/basic';

const { TextArea } = Input;
const { Title, Text } = Typography;

/**
 * (최종 수정) diffArrays가 반환하는 '수정' 덩어리 내부를 다시 한 줄씩
 * 비교하여 각 라인 변경을 별개의 차이점으로 분리합니다.
 * @param {string} leftText
 * @param {string} rightText
 * @returns {{rows: object[], diffBlocks: object[]}}
 */
const processDiffs = (leftText, rightText) => {
    const leftLinesArray = leftText.split('\n');
    const rightLinesArray = rightText.split('\n');
    const differences = diffArrays(leftLinesArray, rightLinesArray);

    const rows = [];
    const diffBlocks = [];
    let leftLineNum = 1;
    let rightLineNum = 1;

    for (let i = 0; i < differences.length; i++) {
        const part = differences[i];
        const nextPart = differences[i + 1];

        if (part.removed && nextPart?.added) {
            const removedLines = part.value;
            const addedLines = nextPart.value;
            const maxLen = Math.max(removedLines.length, addedLines.length);

            for (let j = 0; j < maxLen; j++) {
                const startRow = rows.length;
                const removedLineContent = removedLines[j];
                const addedLineContent = addedLines[j];

                rows.push({
                    left: { lineNumber: removedLineContent !== undefined ? leftLineNum++ : undefined, content: removedLineContent || '', type: 'removed' },
                    right: { lineNumber: addedLineContent !== undefined ? rightLineNum++ : undefined, content: addedLineContent || '', type: 'added' }
                });

                // 각 라인 변경을 별개의 diff로 처리
                diffBlocks.push({ start: startRow, end: startRow });
            }
            i++; // nextPart(added)는 이미 처리했으므로 건너뜀
        } else if (part.removed) {
            const startRow = rows.length;
            part.value.forEach(line => {
                rows.push({
                    left: { lineNumber: leftLineNum++, content: line, type: 'removed' },
                    right: { type: 'placeholder' }
                });
            });
            diffBlocks.push({ start: startRow, end: rows.length - 1 });
        } else if (part.added) {
            const startRow = rows.length;
            part.value.forEach(line => {
                rows.push({
                    left: { type: 'placeholder' },
                    right: { lineNumber: rightLineNum++, content: line, type: 'added' }
                });
            });
            diffBlocks.push({ start: startRow, end: rows.length - 1 });
        } else { // common
            part.value.forEach(line => {
                rows.push({
                    left: { lineNumber: leftLineNum++, content: line, type: 'common' },
                    right: { lineNumber: rightLineNum++, content: line, type: 'common' }
                });
            });
        }
    }
    return { rows, diffBlocks };
};


const SideBySideDiffViewer = ({ result, enableDiffNavigation }) => {
    const { rows, diffBlocks } = result;
    const [currentDiffIndex, setCurrentDiffIndex] = useState(0);
    const rowRefs = useRef([]);

    useEffect(() => {
        setCurrentDiffIndex(0);
    }, [diffBlocks]);

    useEffect(() => {
        if (enableDiffNavigation && diffBlocks.length > 0 && diffBlocks[currentDiffIndex]) {
            const { start } = diffBlocks[currentDiffIndex];
            rowRefs.current[start]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [currentDiffIndex, diffBlocks, enableDiffNavigation]);

    if (diffBlocks.length === 0) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', border: '1px solid #d9d9d9', borderRadius: 4, background: '#f6ffed' }}>
                <Text strong>✅ 두 JSON의 내용이 모두 일치합니다.</Text>
            </div>
        );
    }

    const navigate = (direction) => {
        if (direction === 'prev') {
            setCurrentDiffIndex(i => (i > 0 ? i - 1 : diffBlocks.length - 1));
        } else {
            setCurrentDiffIndex(i => (i < diffBlocks.length - 1 ? i + 1 : 0));
        }
    };

    const isRowInCurrentDiff = (rowIndex) => {
        if (!diffBlocks[currentDiffIndex]) return false;
        const { start, end } = diffBlocks[currentDiffIndex];
        return rowIndex >= start && rowIndex <= end;
    };

    const PaneLine = ({ data, isHighlighted }) => {
        const style = {
            backgroundColor: isHighlighted ? '#fffbdd' : (data.type === 'added' ? '#e6ffed' : (data.type === 'removed' ? '#ffebe9' : 'transparent')),
            width: '50%',
            display: 'flex',
            padding: '0 12px'
        };

        return (
            <div style={style}>
                <span style={{ minWidth: '40px', color: '#aaa', textAlign: 'right', userSelect: 'none' }}>
                    {data.lineNumber}
                </span>
                <span style={{ marginLeft: '12px', flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{data.content}</span>
            </div>
        );
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text strong>Found {diffBlocks.length} differences</Text>
                {enableDiffNavigation && (
                    <Space>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('prev')} />
                        <Text>{currentDiffIndex + 1} of {diffBlocks.length}</Text>
                        <Button icon={<ArrowRightOutlined />} onClick={() => navigate('next')} />
                    </Space>
                )}
            </div>
            <pre style={{ margin: 0, padding: 0, background: '#ffffff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                {rows.map((row, i) => (
                    <div
                        key={i}
                        ref={el => (rowRefs.current[i] = el)}
                        style={{ display: 'flex', minHeight: '22px' }}
                    >
                        <PaneLine data={row.left} isHighlighted={isRowInCurrentDiff(i)} />
                        <PaneLine data={row.right} isHighlighted={isRowInCurrentDiff(i)} />
                    </div>
                ))}
            </pre>
        </div>
    );
};


// 메인 컴포넌트
const JsonDiffViewer = ({ enableDiffNavigation = true }) => {
    const [leftJson, setLeftJson] = useState('');
    const [rightJson, setRightJson] = useState('');
    const [diffResult, setDiffResult] = useState(null);

    const handleCompare = () => {
        let parsedLeft, parsedRight;
        try {
            const sanitizedLeft = sanitizeJson(leftJson);
            parsedLeft = JSON.parse(sanitizedLeft || '{}');
        } catch (e) {
            message.error('왼쪽 JSON의 형식이 올바르지 않습니다.');
            return;
        }
        try {
            const sanitizedRight = sanitizeJson(rightJson);
            parsedRight = JSON.parse(sanitizedRight || '{}');
        } catch (e) {
            message.error('오른쪽 JSON의 형식이 올바르지 않습니다.');
            return;
        }

        const formattedLeft = JSON.stringify(parsedLeft, null, 2);
        const formattedRight = JSON.stringify(parsedRight, null, 2);

        const result = processDiffs(formattedLeft, formattedRight);
        setDiffResult(result);
    };

    return (
        <div style={{ maxWidth: 1600, margin: '0 auto' }}>
            {!diffResult ? (
                <>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        <TextArea value={leftJson} onChange={e => setLeftJson(e.target.value)} rows={20} placeholder="왼쪽 JSON" />
                        <TextArea value={rightJson} onChange={e => setRightJson(e.target.value)} rows={20} placeholder="오른쪽 JSON" />
                    </div>
                    <Button type="primary" onClick={handleCompare}>비교</Button>
                </>
            ) : (
                <div style={{ marginBottom: 16 }}>
                    <Button onClick={() => setDiffResult(null)}>Perform a new diff</Button>
                </div>
            )}
            {diffResult && <SideBySideDiffViewer result={diffResult} enableDiffNavigation={enableDiffNavigation} />}
        </div>
    );
};

export default JsonDiffViewer;